import React, { useCallback, useEffect, useMemo, useReducer } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import Field from 'components/Recommendations/Campaign/Edit/CampaignForm/Field'
import SearchDropdownMultiSelect from 'components/Recommendations/Campaign/Edit/SearchDropdownMultiSelect'
import { initStateArray, arrayReducer, LOADING, OVERWRITE, FAIL } from 'reducers/default'
import { codifyAPIError } from 'utils/helpers'
import googleAudiencesApi from 'domain/googleAudiences/api'
import { toAudienceResourceNames } from 'domain/googleAudiences/mappers'
import { AssetClass, Platform, tcDisplayName } from 'api/models'
import { useCampaignSetup } from 'components/Recommendations/Campaign/Context/CampaignSetupContext'
import {
  GoogleCampaignType,
  deriveCampaignTypeFromSetup,
} from 'components/Recommendations/EventCampaigns/Steps/Setup/Goals/campaignType'

const EXCLUDED_AUDIENCES_ENABLED = false

// One customer-scoped fetch per audience bucket — `type` omitted merges
// remarketing+custom+combined (used for audiences/excluded_audiences);
// `type=saved` is the separate, explicit-only fetch for Demand-Gen-only
// audience_resource_names (see fd-client-api-google-ads' AudiencesAPI).
function useGoogleAudiences(customerId, type) {
  const [state, dispatch] = useReducer(arrayReducer, initStateArray)

  const fetchAudiences = useCallback(async () => {
    if (!customerId) return
    dispatch({ type: LOADING })
    const response = await googleAudiencesApi.fetchAudiencesAsync(customerId, { type })
    if (response.error) {
      dispatch({ type: FAIL, payload: codifyAPIError(response) })
      return
    }
    dispatch({ type: OVERWRITE, payload: response.data })
  }, [customerId, type])

  useEffect(() => {
    fetchAudiences()
  }, [fetchAudiences])

  return [state, fetchAudiences]
}

export function GoogleAudienceTargetingField({ tcTargeting, onChange, disabled, bothSelected }) {
  const { t } = useTranslation()
  const { setup } = useCampaignSetup()

  const customerId = useMemo(
    () =>
      (setup.integration_details || []).find(
        (x) => x.asset_class === AssetClass.ad_account && x.platform === Platform.google,
      )?.asset_id,
    [setup.integration_details],
  )

  const isDemandGen = deriveCampaignTypeFromSetup(setup) === GoogleCampaignType.demandGen

  const [
    {
      items: audienceItems,
      loading: audiencesLoading,
      error: audiencesError,
      message: audiencesMessage,
    },
    refetchAudiences,
  ] = useGoogleAudiences(customerId)

  // For demand gen
  const [
    { items: savedItems, loading: savedLoading, error: savedError, message: savedMessage },
    refetchSaved,
  ] = useGoogleAudiences(isDemandGen ? customerId : null, 'saved')

  const toSelectItem = useCallback((item) => ({ id: item.resource_name, name: item.name }), [])

  // A Demand Gen ad group targets audience segments through the Audience
  // resource the backend composes for it, and AudienceSegment has no
  // combined-audience member — so a combined audience is the one type Demand
  // Gen genuinely cannot target by any mechanism. Everything else (remarketing
  // lists, custom audiences, interests) folds in fine.
  const audienceOptions = useMemo(
    () =>
      (isDemandGen
        ? audienceItems.filter(
            (item) => !(item.resource_name || '').includes('/combinedAudiences/'),
          )
        : audienceItems
      ).map(toSelectItem),
    [audienceItems, isDemandGen, toSelectItem],
  )
  const savedOptions = useMemo(() => savedItems.map(toSelectItem), [savedItems, toSelectItem])

  const audiencesValue = useMemo(() => {
    const ids = (tcTargeting.google?.audiences ?? []).map((a) => a.resource_name)
    return ids.length ? ids.join(',') : null
  }, [tcTargeting.google])

  const excludedValue = useMemo(() => {
    const ids = (tcTargeting.google?.excluded_audiences ?? []).map((a) => a.resource_name)
    return ids.length ? ids.join(',') : null
  }, [tcTargeting.google])

  const resourceNamesValue = useMemo(() => {
    const ids = tcTargeting.google?.audience_resource_names ?? []
    return ids.length ? ids.join(',') : null
  }, [tcTargeting.google])

  const emitGoogle = useCallback(
    (googleChanges) => {
      onChange({ google: { ...tcTargeting.google, ...googleChanges } })
    },
    [onChange, tcTargeting.google],
  )

  const toRichAudienceList = useCallback((idsString, pool) => {
    const ids = (idsString || '').split(',').filter(Boolean)
    return ids
      .map((id) => pool.find((item) => item.resource_name === id))
      .filter(Boolean)
      .map((item) => ({
        resource_name: item.resource_name,
        id: item.id,
        name: item.name,
        type: item.type,
        audience_type: item.audience_type,
        size_for_display: item.size_for_display,
      }))
  }, [])

  const handleAudiencesChange = (idsString) => {
    emitGoogle({ audiences: toRichAudienceList(idsString, audienceItems) })
  }

  const handleExcludedChange = (idsString) => {
    emitGoogle({ excluded_audiences: toRichAudienceList(idsString, audienceItems) })
  }

  const handleResourceNamesChange = (idsString) => {
    emitGoogle({
      audience_resource_names: toAudienceResourceNames(toRichAudienceList(idsString, savedItems)),
    })
  }

  if (!customerId) {
    return null
  }

  const excludedTab = EXCLUDED_AUDIENCES_ENABLED
    ? {
        key: 'excluded_audiences',
        title: t('Recommendations.campaign.setup.audience.googleExcluded'),
        placeholder: t('Recommendations.campaign.setup.audience.placeholder.googleExcluded'),
        options: audienceOptions,
        value: excludedValue,
        onChange: handleExcludedChange,
        loading: audiencesLoading,
        error: audiencesError ? t(audiencesMessage) : null,
        onErrorBtnClick: refetchAudiences,
        showIdSubtext: false,
      }
    : {}

  // Demand Gen: a bare custom/remarketing audience can't be attached as an ad
  // group criterion (the ad group always runs in grouped-audience mode), so
  // saved segments lead as the primary tab. The Include tab is still offered
  // alongside it, because the backend folds those selections into the single
  // Audience resource the ad group carries — what's selected here is what
  // actually targets, rather than silently riding along invisibly on a setup
  // that switched over from Display. Exclusions stay off for Demand Gen:
  // Audience.exclusion_dimension supports user lists only.
  const primaryProps = isDemandGen
    ? {
        value: resourceNamesValue,
        onChange: handleResourceNamesChange,
        options: savedOptions,
        error: { custom: savedError ? t(savedMessage) : null, saved: null },
        loading: { custom: savedLoading, saved: false },
        onErrorBtnClick: refetchSaved,
        tab0Title: t('Recommendations.campaign.setup.audience.googleSavedSegments'),
        tab0Placeholder: t(
          'Recommendations.campaign.setup.audience.placeholder.googleSavedSegments',
        ),
      }
    : {
        value: audiencesValue,
        onChange: handleAudiencesChange,
        options: audienceOptions,
        error: { custom: audiencesError ? t(audiencesMessage) : null, saved: null },
        loading: { custom: audiencesLoading, saved: false },
        onErrorBtnClick: refetchAudiences,
        tab0Title: t('Recommendations.campaign.setup.audience.googleInclude'),
        tab0Placeholder: t('Recommendations.campaign.setup.audience.placeholder.googleInclude'),
      }

  const includeTab = {
    key: 'audiences',
    title: t('Recommendations.campaign.setup.audience.googleInclude'),
    placeholder: t('Recommendations.campaign.setup.audience.placeholder.googleInclude'),
    options: audienceOptions,
    value: audiencesValue,
    onChange: handleAudiencesChange,
    loading: audiencesLoading,
    error: audiencesError ? t(audiencesMessage) : null,
    onErrorBtnClick: refetchAudiences,
    showIdSubtext: false,
    // Demand Gen's Include tab targets alongside saved segments (see the
    // comment above primaryProps), so its selection must surface in the
    // closed-dropdown header too, not just inside the tab itself.
    showInHeader: isDemandGen,
  }

  const extraTabs = isDemandGen ? [includeTab] : [excludedTab]

  return (
    <Field
      fieldName="googleAudience"
      as={SearchDropdownMultiSelect}
      {...primaryProps}
      defaultValue={t('Recommendations.campaign.recommendationTitle', {
        tc: tcDisplayName(tcTargeting),
      })}
      isRequired={false}
      disabled={disabled}
      dropdownClasses="audience google-audience"
      showSavedTab={false}
      showIdSubtext={false}
      extraTabs={extraTabs}
      label={'google'}
      platform="google"
      bothSelected={bothSelected}
    />
  )
}

GoogleAudienceTargetingField.propTypes = {
  tcTargeting: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  bothSelected: PropTypes.bool,
  disabled: PropTypes.bool,
}
